import { Q } from '@fullstory/tomato';
import { ShowHidePanel, Pane } from './showhide';
import { Signal } from '@fullstory/util/signal';
import { HistoryNode } from '@fullstory/history';
import { SelectionListener, SelectListPanel, ListAdapter } from './lists';
import { Capture, CaptureEnabled } from "./capture";
// Don't import views.ts here; if you need to do that, put your code in specializeddialogs.ts instead.

export const DefaultHideAnimationDuration = 250;

export enum PopupPosition {
  MAYBE_BELOW_RIGHT = 1,
  ABOVE_RIGHT,
  MAYBE_BELOW_LEFT,
  ABOVE_LEFT,
  RIGHT,
  LEFT,
  MAYBE_BELOW_CENTER,
  ABOVE_CENTER,
  BELOW_LEFT,
  BELOW_RIGHT,
  BELOW_CENTER,
  INSIDE_LEFT,
}

// Returns the nearest ancestor to e that has a computed value not equal to "none"
// for the transform property. If no such element is found before reaching document.body,
// it returns null. e is expected to be a descendant of document.body.
function nearestAncestorWithTransform(e: HTMLElement): HTMLElement | null {
  var p = e.parentElement;
  while (p && p != document.body && p instanceof Element) {
    if (window.getComputedStyle(p).getPropertyValue("transform") != "none") {
      return p;
    }
    p = p.parentElement;
  }
  return null;
}

// Use this class whenever you want a fragile popup (or menu) that is potentially triggered by a button
// and has to be positioned next to the button.
export class FragilePopup<V extends Q.OfOne> extends ShowHidePanel<V> implements Pane, CaptureEnabled {
  protected triggerElem: Q.OfOne | null = null;
  protected positionElem: Q.OfOne | null = null;
  private pos: number = 0;
  private onShowSignal = new Signal();
  private onHiddenSignal = new Signal();
  protected triggerDisabled: boolean = false;
  private _partners: Q.OfOne[] = [];
  protected _topPad = 5;
  protected _sidePad = 10;

  // Lazily initialized.
  _parent: Q.OfOne | null = null;

  constructor(view: V, classList: string[] = ["fragilePopup", "fadeable"]) {
    super(view);
    classList.forEach((c) => { this.view().addClass(c) });
    this.setAnimationDuration(DefaultHideAnimationDuration);
  }

  contains(elem: HTMLElement): boolean {
    if (this.view().contains(elem)) {
      return true;
    }
    if (this._isPartner(elem)) {
      return true;
    }
    const trigger = this.trigger();
    if (trigger) {
      return trigger.contains(elem);
    }
    return false;
  }

  addPartner(dontHideIfThisIsClicked: Q.OfOne) {
    this._partners.push(dontHideIfThisIsClicked);
  }

  private _isPartner(elem: HTMLElement): boolean {
    for (var i = 0; i < this._partners.length; i++) {
      if (this._partners[i]?.contains(elem)) {
        return true;
      }
    }
    return false;
  }

  onClickOff() {
    this.hide();
  }

  triggeredBy(trigger: Q.OfOne, position?: number): this {
    this.triggerElem = trigger;
    this.triggerElem.on('click', () => {
      // TODO(sarahgrace): Checking triggerDisabled doesn't quite work. The dialog will reopen when already open.
      if (!this.triggerDisabled) { this.toggle() };
    });

    if (position) { // optionally also use the trigger as a positioning element.
      this.position(position, trigger);
    }
    return this;
  }

  position(position: number, nextTo: Q.OfOne | null): this {
    this.pos = position;
    this.positionElem = nextTo;
    return this;
  }

  reposition() {
    this._position(this.pos);
  }

  appendTo(parent: Q.OfOne): this {
    this._parent = parent;
    return this;
  }

  parent(): Q.OfOne | null {
    return this._parent;
  }

  trigger(): Q.OfOne | null {
    return this.triggerElem;
  }

  onShow(l: () => void): void {
    this.onShowSignal.tap(l);
  }

  onHidden(l: () => void): void {
    this.onHiddenSignal.tap(l);
  }

  showImpl() {
    if (this._parent) {
      this.view().appendTo(this._parent);
    }
    this.view().setCss("display", "inline-block");
    this.reposition();
    if (this.triggerElem) {
      this.triggerElem.addClass('active');
    }
    this.onShowSignal.raise();
    Capture.start(this);
  }

  showAsync() {
    this.view().setCss("opacity", "1")
  }

  hideImpl() {
    this.view().setCss("opacity", "0");
    if (this.triggerElem) { this.triggerElem.removeClass('active'); }
    Capture.release(this);
  }

  afterHide() {
    this.view().setCss("display", "none");
    if (this._parent) { this.view().remove() }
    this.onHiddenSignal.raise();
    this.hidden();
  }

  // Override this to add customization.
  hidden() { }

  disableTrigger(disabled: boolean) {
    this.triggerDisabled = disabled;
  }

  setPadding(top: number, side: number) {
    this._topPad = top;
    this._sidePad = side;
  }

  protected _position(pos: number) {
    if (!pos || !this.positionElem) { return }

    var rect = this.positionElem.elem().getBoundingClientRect();
    var right = window.innerWidth;
    var bottom = window.innerHeight;
    var left = 0;

    // Because we use "position: fixed" to position the element, if the element to
    // be positioned has an ancestor with a computed value other than "none" for the
    // transform property, the ancestor acts as the containing block for the fixed positioned
    // descendant. See https://www.w3.org/TR/css-transforms-1/#transform-property.
    //
    // If such an ancestor exists, compute the actual values for right and bottom.
    var transformEl = nearestAncestorWithTransform(this.view().elem());
    if (transformEl != null) {
      left = transformEl.getBoundingClientRect().left;
      right = transformEl.getBoundingClientRect().right;
      bottom = transformEl.getBoundingClientRect().bottom;
    }

    var viewRect = this.view().bounds();
    var viewWidth = viewRect.right - viewRect.left;
    var viewHeight = viewRect.bottom - viewRect.top;

    switch (pos) {
      // Lines up the panel right edge with the positionElem right edge.
      case PopupPosition.MAYBE_BELOW_RIGHT:
        this.view().setCss('position', 'fixed');
        this.view().setCss('right', right - rect.right);
        this._maybeAnchorBelow(rect, bottom, viewHeight);
        break;
      case PopupPosition.ABOVE_RIGHT:
        this.view().setCss('position', 'fixed');
        this.view().setCss('right', right - rect.right);
        this._anchorAbove(rect, bottom);
        break;
      // Lines up the FragilePopup left edge with the positionElem left edge.
      case PopupPosition.MAYBE_BELOW_LEFT:
        this.view().setCss('position', 'fixed');
        this.view().setCss('left', rect.left);
        this._maybeAnchorBelow(rect, bottom, viewHeight);
        break;
      case PopupPosition.ABOVE_LEFT:
        this.view().setCss('position', 'fixed');
        this.view().setCss('left', rect.left);
        this._anchorAbove(rect, bottom);
        break;
      case PopupPosition.RIGHT:
        this.view().setCss('position', 'fixed');
        this.view().setCss('left', rect.right + this._sidePad);
        this._setTopCss(rect.top);
        break;
      case PopupPosition.LEFT:
        this.view().setCss('position', 'fixed');
        this.view().setCss('right', rect.left + this._sidePad);
        this._setTopCss(rect.top);
        break;
      case PopupPosition.ABOVE_CENTER:
        this.view().setCss('position', 'fixed');
        this.view().setCss('left', (((rect.right + rect.left) / 2) - (viewWidth / 2)) - left);
        this._anchorAbove(rect, bottom);
        break;
      case PopupPosition.MAYBE_BELOW_CENTER:
        this.view().setCss('position', 'fixed');
        this.view().setCss('left', (((rect.right + rect.left) / 2) - (viewWidth / 2)) - left);
        this._maybeAnchorBelow(rect, bottom, viewHeight);
        break;
      case PopupPosition.BELOW_RIGHT:
        this.view().setCss('position', 'fixed');
        this.view().setCss('right', right - rect.right);
        this._setTopCss(rect.bottom + this._topPad);
        break;
      case PopupPosition.BELOW_LEFT:
        this.view().setCss('position', 'fixed');
        this.view().setCss('left', rect.left);
        this._setTopCss(rect.bottom + this._topPad);
        break;
      case PopupPosition.BELOW_CENTER:
        this.view().setCss('position', 'fixed');
        this.view().setCss('left', (((rect.right + rect.left) / 2) - (viewWidth / 2)) - left);
        this._setTopCss(rect.bottom + this._topPad);
        break;

    }
  }

  /**
   * Positions the dialog below the rect if there is room. Otherwise it positions it above the rect.
   *
   * @param {ClientRect} rect bounds to position the dialog against
   * @param {number} bottom the bottom coordinates
   * @param {number} viewHeight the dialog's actual height
   * @private
   */
  private _maybeAnchorBelow(rect: ClientRect, bottom: number, viewHeight: number) {
    let anchor = rect.bottom + this._topPad;

    // check to see if the dialog will be partially off-screen but not if it won't fit above
    if (anchor + viewHeight > bottom && viewHeight < anchor) {
      // position this above the control, since it would be off-screen otherwise
      this._anchorAbove(rect, bottom);
    } else {
      this._setTopCss(anchor);
    }
  }

  /**
   * Positions the dialog above the rect.
   *
   * @param {ClientRect} rect bounds to position the dialog against
   * @param {number} bottom the bottom coordinates
   * @private
   */
  private _anchorAbove(rect: ClientRect, bottom: number) {
    this._setBottomCss(bottom - rect.top + this._topPad);
  }

  private _setTopCss(top: number) {
    this.view().setCss('top', top);
    // null out the 'bottom' value - it might have been set by a previous anchoring
    this.view().setCss('bottom', null);
  }

  private _setBottomCss(bottom: number) {
    this.view().setCss('bottom', bottom);
    // null out the 'top' value - it might have been set by a previous anchoring
    this.view().setCss('top', null);
  }

  // Used by the capture system to determine if we should elide the hide call.
  // Implementors can override the capture auto-hiding system by overriding this method.
  hideFromCapture(): boolean {
    return true;
  }
}

// Similar to a FragilePopup, but with show/hide semantics that are more suited to pairing with an input box.
// The trigger element must be of type HTMLInputElement.
export class InputPopup<V extends Q.OfOne> extends FragilePopup<V> {
  triggeredBy(trigger: Q.OfOne, position?: number): this {
    this.triggerElem = trigger;
    this.triggerElem.on('click', () => {
      if (!this.triggerDisabled) { this.show(); }
    });

    this.triggerElem.on('focus', () => {
      if (!this.triggerDisabled) { this.show(); }
    });

    if (position) { // optionally also use the trigger as a positioning element.
      this.position(position, trigger);
    }
    return this;
  }
}

// A dialog with a glass blocking the full view. Can be implemented to have history.
export class DialogWithGlass<V extends Q.OfOne> extends FragilePopup<V> implements Pane {
  glass: Q.OfOne | null = null;
  private _hostExisted: boolean = false;
  private _fadeIn!: boolean;

  constructor(view: V, classList: string[] = ["DialogWithGlass"], _container: HTMLElement = document.body, private _showGlass = true, fadeIn = true) {
    super(view, classList);
    if (this._showGlass) { this.glass = Q.createOne('div').setClass('DialogGlass') }
    this._parent = new Q.OfOne(_container);
    this.setFadeIn(fadeIn);
  }

  setFadeIn(fadeIn: boolean) {
    this._fadeIn = fadeIn;
    this.view().setCss("opacity", this._fadeIn ? "0" : "")
  }

  // Override this to add customizations.
  // Called immediately AFTER the panel is shown, on the next dispatch of the browser event loop
  // to allow for animation of property sets.
  showAsync() {
    // The default is to fade in the glass and popup
    if (this.glass) { this.glass.setCss('opacity', 0.25); }
    if (this._fadeIn) { this.view().setCss('opacity', 1) };
  }

  // Override this to add customizations.
  // Called immediately before we queue a timeout to hide this panel, to allow for
  // animations to take place before removing the panel from the DOM.
  hideImpl() {
    // The default is to fade out the glass and popup.
    if (this.glass) { this.glass.setCss('opacity', 0) }
    if (this._fadeIn) { this.view().setCss('opacity', 0) };
    Capture.release(this);
  }

  showImpl() {
    const parent = this.parent();
    if (this.glass && parent) { this.glass.appendTo(parent) }
    super.showImpl();
  }

  // Called after the panel hide animations are completed, and after the panel is removed from the DOM.
  afterHide() {
    if (this.glass) { this.glass.remove() }
    super.afterHide();
  }

  // showDetail and backForward should be implemented to enable history.
  // Override for history
  showDetail(history: HistoryNode, detailId: string) {
    // do nothing by default
  }

  // "Back out" by going forward. Usually implemented as something like "history.parent.navigate([])".
  backForward() {
    // do nothing by default
  }

  // Override and return false to prevent hide on capture
  hideFromCapture(): boolean {
    // Use history to hide if overriding dialog has set it up.
    if (this._hostExisted) { // Whether or not we have something to go back to.
      history.back();
    } else {
      // Move history forward. Without this child piece dangling off it.
      // Will do nothing if backForward() is not implemented
      this.backForward();
    }
    // If we already went "back" this is a no-op, otherwise we close on capture by default.
    return true;
  }

  // NOTE(jgw): This is a hack to fix dismissing playing nice with history, when linking to a user detail.
  // This wouldn't be an issue if dismissing always moved history forward. But that would make a mess of
  // the history stack every time you open/close a user detail.
  // TODO(jaime): Figure out a nice way to remove this. It's smelly.
  notifyHostExisted() {
    this._hostExisted = true;
  }
}

// Dialog with optional glass background that by default slides in from the right.
export class SidecarDialog<V extends Q.OfOne> extends DialogWithGlass<V> implements Pane {
  constructor(view: V, classList: string[] = ["DialogContent"], container: HTMLElement = document.body, showGlass = true) {
    super(view, classList, container, showGlass, false);
  }

  showAsync() {
    super.showAsync();
    this.view().setCssTransform('translateX(0)');
  }

  hideImpl() {
    super.hideImpl();
    this.view().setCssTransform('translateX(100%)');
  }
}

// A panel that contains a list of selectable items. Use this if you have a data-driven list of things that need
// to be rendered in a fragile popup and are optionally keyboard navigable and selectable.
// TODO(dave): We should probably just move this mode into the base SelectionPopup, so we dont have future confusion
//  over which dialog class to use.
export class SelectionPopup<I, V extends Q.OfOne> extends FragilePopup<Q.OfOne> {
  _selectionPanel: SelectListPanel<I, V>;
  _deselectOnHide = true;
  _deselectOnUpdate = false;

  constructor(view: V, adapter: ListAdapter<I, V>) {
    super(view);
    this._selectionPanel = new SelectListPanel<I, V>(view, adapter);
  }

  model(): SelectListPanel<I, V> {
    return this._selectionPanel;
  }

  deselectAll() {
    this._selectionPanel.deselectAll();
  }

  addSelectionListener(l: SelectionListener<I, V>): () => void {
    return this._selectionPanel.addSelectionListener(l);
  }

  render(items: I[]) {
    if (this._deselectOnUpdate) { this._selectionPanel.deselectAll(); }
    this._selectionPanel.update(items);
  }

  viewById(id: string): V | null {
    return this._selectionPanel.viewById(id);
  }

  hidden() {
    if (this._deselectOnHide) { this._selectionPanel.deselectAll() }
  }

  // If inputTrigger is set to true, the trigger element is an input box and the triggeredBy method will use semantics
  // similar to InputPopup to show/hide the popup.
  triggeredBy(trigger: Q.OfOne, position?: number, inputTrigger?: boolean): this {
    this._selectionPanel.addKeyboardNav(trigger);

    if (inputTrigger) {
      this.triggerElem = trigger;
      this.triggerElem.on('click', () => {
        if (!this.triggerDisabled) { this.show(); };
      });

      this.triggerElem.on('focus', () => {
        if (!this.triggerDisabled) { this.show(); };
      });

      this.triggerElem.on('blur', () => {
        if (!this.triggerDisabled) { this.hide(); };
      });

      if (position) { // optionally also use the trigger as a positioning element.
        this.position(position, trigger);
      }
    } else {
      super.triggeredBy(trigger, position);
    }

    return this;
  }

  position(position: number, nextTo: Q.OfOne): this {
    super.position(position, nextTo);
    return this;
  }

  appendTo(parent: Q.OfOne): this {
    super.appendTo(parent);
    return this;
  }

  deselectOnHide(deselect: boolean): SelectionPopup<I, V> {
    this._deselectOnHide = deselect;
    return this;
  }

  deselectOnUpdate(deselect: boolean): SelectionPopup<I, V> {
    this._deselectOnUpdate = deselect;
    return this;
  }
}

// A wrapper for SelectionPopup that can be shared between multiple trigger elements and routes the selection listener
//  to the specific listener associated with the specific trigger element that most recently activated the popup
export class SelectionPopupMultiTrigger<I, V extends Q.OfOne> {
  private _currentKey: any;
  private _keys: any[] = [];
  private _listeners: SelectionListener<I, V>[] = [];

  private _selectionRouter: SelectionListener<I, V> = (
    id: string,
    item: I | null,
    view: V | null
  ) => {
    const index = this._keys.indexOf(this._currentKey);
    if (index >= 0) {
      this._listeners[index]?.(id, item, view);
    }
  };

  constructor(private _selectPanel: SelectionPopup<I, V>) {
    this._selectPanel.addSelectionListener(this._selectionRouter);
  }

  panel(): SelectionPopup<I, V> {
    return this._selectPanel;
  }

  addTrigger(key: any, trigger: Q.OfOne, position: number) {
    trigger.on("click", (e) => {
      this._currentKey = key;
      // MUSTDO(dave): Since we keep a single top level panel ref for each type and want to show it on different conditions when clicked
      //  we are -not- using triggeredBy - make sure this is copacetic with how panels are expected to be used
      //  One thing I havent done yet is registered it for keyboard nav, need to re-visit that...
      this._selectPanel.position(position, trigger);
      this._selectPanel.toggle();
    });
  }

  addSelectionListener(key: any, listener: SelectionListener<I, V>) {
    this._keys.push(key);
    this._listeners.push(listener);
  }

  removeSelectionListenerByKey(key: any) {
    var index = this._keys.indexOf(key);
    if (index >= 0) {
      this._keys.splice(index, 1);
      this._listeners.splice(index, 1);
    }
  }

  clearSelectionListeners() {
    this._keys = [];
    this._listeners = [];
  }

  getCurrentKey(): any {
    return this._currentKey;
  }
}
